import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import SectionHeader from '../components/SectionHeader';
import { fmtCost } from '../utils/format';

const currentMonthValue = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const SupplierSpend = () => {
  const navigate = useNavigate();
  const [monthValue, setMonthValue] = useState(currentMonthValue());
  const [suppliers, setSuppliers] = useState([]);
  const [selected, setSelected]   = useState(() => new Set());
  const [loading, setLoading]     = useState(true);

  const load = useCallback(async (month) => {
    setLoading(true);
    const { data, error } = await supabase.rpc('get_supplier_monthly_spend', {
      p_month_start: `${month}-01`,
    });
    if (!error) {
      setSuppliers(data || []);
      setSelected(new Set((data || []).map(r => r.supplier)));
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(monthValue); }, [monthValue, load]);

  const toggle = (supplier) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(supplier)) next.delete(supplier);
      else next.add(supplier);
      return next;
    });
  };

  const grandTotal    = suppliers.reduce((s, r) => s + Number(r.total_owing || 0), 0);
  const selectedRows  = suppliers.filter(r => selected.has(r.supplier));
  const selectedTotal = selectedRows.reduce((s, r) => s + Number(r.total_owing || 0), 0);
  const monthLabel    = new Date(`${monthValue}-15`).toLocaleDateString('en-NZ', { month: 'long', year: 'numeric' });

  return (
    <main className="app-main">
      <div className="page-nav">
        <button className="back-btn" onClick={() => navigate('/utilities')}>← Utilities</button>
        <span className="page-nav__title">Supplier Spend</span>
        <button
          className="app-header__btn"
          onClick={() => navigate(`/supplier-pos?supplier=__all__&month=${monthValue}`)}
          title="List every purchase order included in this month's totals"
        >
          📋 All Purchase Orders
        </button>
        <button className="app-header__btn" onClick={() => load(monthValue)}>↻ Refresh</button>
      </div>

      <section className="section">
        <SectionHeader
          title={`Purchase Order Spend — ${monthLabel}`}
          count={suppliers.length}
          onRefresh={() => load(monthValue)}
          loading={loading}
        />
        <p className="section__desc">
          Total value of purchase order lines raised this calendar month, by supplier.
          Select suppliers on the left to see their combined total on the right.
        </p>

        <div style={{ marginBottom: 16 }}>
          <label style={{ marginRight: 8, fontWeight: 600 }}>Month:</label>
          <input
            type="month"
            value={monthValue}
            max={currentMonthValue()}
            onChange={(e) => setMonthValue(e.target.value)}
            style={{ padding: '6px 10px' }}
          />
        </div>

        {loading && <div className="loading-row">Loading…</div>}

        {!loading && (
          <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 360px', minWidth: 320 }}>
              <div style={{
                border: '1px solid var(--border)', borderRadius: 10,
                background: 'var(--bg2)', overflow: 'hidden',
              }}>
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  padding: '10px 16px', fontWeight: 600,
                  borderBottom: '1px solid var(--border)',
                }}>
                  <span>All Suppliers</span>
                  <span>{fmtCost(grandTotal)}</span>
                </div>
                {suppliers.map(row => (
                  <label
                    key={row.supplier}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 16px', cursor: 'pointer',
                      borderBottom: '1px solid var(--border)',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(row.supplier)}
                      onChange={() => toggle(row.supplier)}
                    />
                    <span style={{ flex: 1 }}>{row.supplier}</span>
                    <span>{fmtCost(row.total_owing)}</span>
                    <button
                      type="button"
                      className="app-header__btn"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/supplier-pos?supplier=${encodeURIComponent(row.supplier)}&month=${monthValue}`); }}
                      title="View purchase orders"
                    >
                      View POs
                    </button>
                  </label>
                ))}
                {!suppliers.length && (
                  <div className="empty-row">No purchase order activity this month</div>
                )}
              </div>
            </div>

            <div style={{ flex: '1 1 280px', minWidth: 260 }}>
              <div style={{
                border: '1px solid var(--border)', borderRadius: 10,
                background: 'var(--bg2)', padding: '16px 20px',
              }}>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>
                  Selected ({selectedRows.length})
                </div>
                {selectedRows.length === 0 && (
                  <div className="empty-row">Check suppliers on the left to total them here</div>
                )}
                {selectedRows.map(row => (
                  <div key={row.supplier} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                    <span>{row.supplier}</span>
                    <span>{fmtCost(row.total_owing)}</span>
                  </div>
                ))}
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  marginTop: 12, paddingTop: 12,
                  borderTop: '1px solid var(--border)',
                  fontWeight: 700, fontSize: '1.1em',
                }}>
                  <span>Total</span>
                  <span>{fmtCost(selectedTotal)}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </section>
    </main>
  );
};

export default SupplierSpend;
